/**
 * OpenWorldMapLoader - Lädt und rendert OpenStreetMap Daten für Baden-Württemberg
 * 
 * Verwendet Overpass API für Echtzeit-OSM-Daten oder vorgeladene GeoJSON-Tiles
 * für realistisches Open World Fahren
 */

import * as THREE from 'three';
import { StreetFurnitureSystem } from './StreetFurnitureSystem';
import { EnvironmentMaterialController } from './EnvironmentMaterialController';

// Baden-Württemberg Bounding Box
const BW_BOUNDS = {
  minLat: 47.5324,
  maxLat: 49.7913,
  minLon: 7.5113,
  maxLon: 10.4918,
  // Zentrum: Stuttgart
  centerLat: 48.7758,
  centerLon: 9.1829
};

// Straßentypen nach OSM
export type RoadType = 
  | 'motorway'      // Autobahn
  | 'trunk'         // Schnellstraße
  | 'primary'       // Bundesstraße
  | 'secondary'     // Landesstraße
  | 'tertiary'      // Kreisstraße
  | 'residential'   // Wohngebiet
  | 'service'       // Zufahrt
  | 'unclassified'; // Sonstige

export interface RoadSegment {
  id: string;
  type: RoadType;
  name?: string;
  speedLimit?: number;
  lanes?: number;
  points: THREE.Vector3[];
  width: number;
}

export interface MapTile {
  x: number;
  y: number;
  zoom: number;
  roads: RoadSegment[];
  buildings: BuildingData[];
  loaded: boolean;
}

export interface BuildingData {
  id: string;
  footprint: THREE.Vector3[];
  height: number;
  type?: string;
}

// Straßen-Styling nach Typ
const ROAD_STYLES: Record<RoadType, { width: number; color: number; speedLimit: number; lanes: number }> = {
  motorway:     { width: 18, color: 0x2b2f33, speedLimit: 130, lanes: 3 },  // +4m (breit genug für Wenden)
  trunk:        { width: 14, color: 0x30343b, speedLimit: 100, lanes: 2 },  // +2m
  primary:      { width: 12, color: 0x353a40, speedLimit: 100, lanes: 2 },  // +2m
  secondary:    { width: 10, color: 0x3a4047, speedLimit: 70,  lanes: 2 },  // +2m
  tertiary:     { width: 8,  color: 0x40464d, speedLimit: 50,  lanes: 1 },  // +2m
  residential:  { width: 7,  color: 0x3c424a, speedLimit: 30,  lanes: 1 },  // +2m (Wenden möglich)
  service:      { width: 6,  color: 0x454b54, speedLimit: 20,  lanes: 1 },  // +2m (Mindestens für Wenden)
  unclassified: { width: 7,  color: 0x3f454c, speedLimit: 50,  lanes: 1 }   // +2m
};

// Bekannte Städte in BW für schnellen Zugriff
export const BW_CITIES = {
  stuttgart:    { lat: 48.7758, lon: 9.1829,  name: 'Stuttgart' },
  karlsruhe:    { lat: 49.0069, lon: 8.4037,  name: 'Karlsruhe' },
  mannheim:     { lat: 49.4875, lon: 8.4660,  name: 'Mannheim' },
  freiburg:     { lat: 47.9990, lon: 7.8421,  name: 'Freiburg' },
  heidelberg:   { lat: 49.3988, lon: 8.6724,  name: 'Heidelberg' },
  ulm:          { lat: 48.4011, lon: 9.9876,  name: 'Ulm' },
  heilbronn:    { lat: 49.1427, lon: 9.2109,  name: 'Heilbronn' },
  pforzheim:    { lat: 48.8922, lon: 8.6947,  name: 'Pforzheim' },
  reutlingen:   { lat: 48.4914, lon: 9.2043,  name: 'Reutlingen' },
  tuebingen:    { lat: 48.5216, lon: 9.0576,  name: 'Tübingen' },
  konstanz:     { lat: 47.6633, lon: 9.1753,  name: 'Konstanz' },
  offenburg:    { lat: 48.4721, lon: 7.9408,  name: 'Offenburg' },
  sindelfingen: { lat: 48.7130, lon: 9.0026,  name: 'Sindelfingen' },
  boeblingen:   { lat: 48.6863, lon: 9.0146,  name: 'Böblingen' },
  esslingen:    { lat: 48.7406, lon: 9.3108,  name: 'Esslingen' }
};

export class OpenWorldMapLoader {
  private scene: THREE.Scene;
  private loadedTiles: Map<string, MapTile> = new Map();
  private roadMeshes: Map<string, THREE.Mesh> = new Map();
  private buildingMeshes: Map<string, THREE.Mesh> = new Map();
  private currentPosition = { lat: BW_CITIES.stuttgart.lat, lon: BW_CITIES.stuttgart.lon };
  private metersPerDegree = 111320; // Ungefähre Umrechnung
  private tileSize = 0.01; // ~1.1km pro Tile
  private loadRadius = 3; // Tiles um Spieler herum laden
  
  // Materialien
  private roadMaterials: Map<RoadType, THREE.MeshStandardMaterial> = new Map();
  private roadMarkMaterial!: THREE.MeshStandardMaterial;
  private roadEdgeMaterial!: THREE.MeshStandardMaterial;
  private roadDecorations: THREE.Object3D[] = [];
  private buildingMaterial!: THREE.MeshStandardMaterial;
  private groundMaterial!: THREE.MeshStandardMaterial;
  
  // Ground plane
  private groundPlane!: THREE.Mesh;
  
  // Environment Controllers
  private envMatCtrl = new EnvironmentMaterialController();
  private furniture: StreetFurnitureSystem | null = null;
  
  private nightFactor = 0;  // 0 day .. 1 night
  private wetness = 0;      // 0 dry .. 1 wet
  
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.initMaterials();
    this.createGround();
    
    // street furniture (caps so it doesn't explode)
    this.furniture = new StreetFurnitureSystem(this.scene, {
      maxLights: 3500,
      maxSignals: 900
    });
  }
  
  private initMaterials(): void {
    // Straßen-Materialien
    for (const [type, style] of Object.entries(ROAD_STYLES)) {
      const material = new THREE.MeshStandardMaterial({
        color: style.color,
        roughness: 0.9,
        metalness: 0.1,
        side: THREE.DoubleSide
      });
      this.roadMaterials.set(type as RoadType, material);
      this.envMatCtrl.registerRoadMaterial(material);
    }
    
    // Gebäude-Material
    this.buildingMaterial = new THREE.MeshStandardMaterial({
      color: 0x8b9dc3,
      roughness: 0.7,
      metalness: 0.2
    });
    
    // Boden-Material (Gras/Wiese)
    this.groundMaterial = new THREE.MeshStandardMaterial({
      color: 0x3d5c3d,
      roughness: 1.0,
      metalness: 0.0
    });

    this.roadMarkMaterial = new THREE.MeshStandardMaterial({
      color: 0xf2f2f2,
      roughness: 0.4,
      metalness: 0.05,
      emissive: 0x111111
    });

    this.roadEdgeMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4d4d4,
      roughness: 0.6,
      metalness: 0.05
    });
  }
  
  private createGround(): void {
    // Großer Boden für Baden-Württemberg (ca. 300km x 250km)
    const groundSize = 400000; // 400km in Metern
    const geometry = new THREE.PlaneGeometry(groundSize, groundSize, 100, 100);
    geometry.rotateX(-Math.PI / 2);
    
    // Leichte Hügel für Realismus
    const positions = geometry.getAttribute('position');
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const z = positions.getZ(i);
      // Sanfte Hügel basierend auf Noise
      const noise = Math.sin(x * 0.00005) * Math.cos(z * 0.00005) * 50;
      const noise2 = Math.sin(x * 0.0002) * Math.cos(z * 0.0002) * 10;
      positions.setY(i, noise + noise2);
    }
    geometry.computeVertexNormals();
    
    this.groundPlane = new THREE.Mesh(geometry, this.groundMaterial);
    this.groundPlane.receiveShadow = true;
    this.scene.add(this.groundPlane);
  }
  
  /**
   * Konvertiert GPS-Koordinaten zu 3D-Weltkoordinaten
   */
  gpsToWorld(lat: number, lon: number): THREE.Vector3 {
    const x = (lon - this.currentPosition.lon) * this.metersPerDegree * Math.cos(lat * Math.PI / 180);
    const z = -(lat - this.currentPosition.lat) * this.metersPerDegree;
    return new THREE.Vector3(x, 0, z);
  }
  
  /**
   * Konvertiert 3D-Weltkoordinaten zu GPS
   */
  worldToGps(position: THREE.Vector3): { lat: number; lon: number } {
    const lat = this.currentPosition.lat - position.z / this.metersPerDegree;
    const lon = this.currentPosition.lon + position.x / (this.metersPerDegree * Math.cos(lat * Math.PI / 180));
    return { lat, lon };
  }
  
  /**
   * Lädt Karten-Tiles um eine Position herum
   */
  async loadAroundPosition(worldPos: THREE.Vector3): Promise<void> {
    const gps = this.worldToGps(worldPos);
    
    // Berechne welche Tiles geladen werden müssen
    const centerTileX = Math.floor(gps.lon / this.tileSize);
    const centerTileY = Math.floor(gps.lat / this.tileSize);
    
    const tilesToLoad: { x: number; y: number }[] = [];
    
    for (let dx = -this.loadRadius; dx <= this.loadRadius; dx++) {
      for (let dy = -this.loadRadius; dy <= this.loadRadius; dy++) {
        const tileX = centerTileX + dx;
        const tileY = centerTileY + dy;
        const tileKey = `${tileX}_${tileY}`;
        
        if (!this.loadedTiles.has(tileKey)) {
          tilesToLoad.push({ x: tileX, y: tileY });
        }
      }
    }
    
    // Lade fehlende Tiles
    for (const tile of tilesToLoad) {
      await this.loadTile(tile.x, tile.y);
    }
    
    // Entferne weit entfernte Tiles (Speicher sparen)
    this.cleanupDistantTiles(centerTileX, centerTileY);
  }
  
  /**
   * Lädt ein einzelnes Tile von OSM
   */
  private async loadTile(tileX: number, tileY: number): Promise<void> {
    const tileKey = `${tileX}_${tileY}`;
    
    // Bounding Box für dieses Tile
    const minLon = tileX * this.tileSize;
    const maxLon = (tileX + 1) * this.tileSize;
    const minLat = tileY * this.tileSize;
    const maxLat = (tileY + 1) * this.tileSize;
    
    // Prüfe ob innerhalb BW
    if (minLon < BW_BOUNDS.minLon || maxLon > BW_BOUNDS.maxLon ||
        minLat < BW_BOUNDS.minLat || maxLat > BW_BOUNDS.maxLat) {
      // Außerhalb BW - leeres Tile
      this.loadedTiles.set(tileKey, {
        x: tileX, y: tileY, zoom: 15,
        roads: [], buildings: [], loaded: true
      });
      return;
    }
    
    try {
      // Overpass API Query für Straßen (für Produktion)
      // const query = `...`
      // fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`)
      
      // Für Demo: Generiere prozedurale Straßen statt API-Call
      const roads = this.generateProceduralRoads(minLat, minLon, maxLat, maxLon);
      const buildings = this.generateProceduralBuildings(minLat, minLon, maxLat, maxLon);
      
      const tile: MapTile = {
        x: tileX, y: tileY, zoom: 15,
        roads, buildings, loaded: true
      };
      
      this.loadedTiles.set(tileKey, tile);
      
      // Rendere das Tile
      this.renderTile(tile);
      
    } catch (error) {
      console.error(`Failed to load tile ${tileKey}:`, error);
      // Leeres Tile bei Fehler
      this.loadedTiles.set(tileKey, {
        x: tileX, y: tileY, zoom: 15,
        roads: [], buildings: [], loaded: true
      });
    }
  }
  
  /**
   * Generiert prozedurale Straßen für Demo (ohne echte OSM-Daten)
   */
  private generateProceduralRoads(minLat: number, minLon: number, maxLat: number, maxLon: number): RoadSegment[] {
    const roads: RoadSegment[] = [];
    const rng = this.seededRandom(minLat * 1000 + minLon * 100);
    
    // Hauptstraße (horizontal)
    const mainRoadY = minLat + (maxLat - minLat) * 0.5;
    roads.push({
      id: `main_h_${minLat}_${minLon}`,
      type: rng() > 0.7 ? 'primary' : 'secondary',
      name: 'Hauptstraße',
      speedLimit: 50,
      lanes: 2,
      width: ROAD_STYLES.primary.width,
      points: [
        this.gpsToWorld(mainRoadY, minLon),
        this.gpsToWorld(mainRoadY, maxLon)
      ]
    });
    
    // Hauptstraße (vertikal)
    const mainRoadX = minLon + (maxLon - minLon) * 0.5;
    roads.push({
      id: `main_v_${minLat}_${minLon}`,
      type: rng() > 0.7 ? 'primary' : 'secondary',
      name: 'Kreuzstraße',
      speedLimit: 50,
      lanes: 2,
      width: ROAD_STYLES.primary.width,
      points: [
        this.gpsToWorld(minLat, mainRoadX),
        this.gpsToWorld(maxLat, mainRoadX)
      ]
    });
    
    // Nebenstraßen (Wohngebiet-Grid)
    const gridSize = 5;
    for (let i = 1; i < gridSize; i++) {
      const lat = minLat + (maxLat - minLat) * (i / gridSize);
      const lon = minLon + (maxLon - minLon) * (i / gridSize);
      
      if (rng() > 0.3) {
        roads.push({
          id: `res_h_${i}_${minLat}_${minLon}`,
          type: 'residential',
          speedLimit: 30,
          lanes: 1,
          width: ROAD_STYLES.residential.width,
          points: [
            this.gpsToWorld(lat, minLon),
            this.gpsToWorld(lat, maxLon)
          ]
        });
      }
      
      if (rng() > 0.3) {
        roads.push({
          id: `res_v_${i}_${minLat}_${minLon}`,
          type: 'residential',
          speedLimit: 30,
          lanes: 1,
          width: ROAD_STYLES.residential.width,
          points: [
            this.gpsToWorld(minLat, lon),
            this.gpsToWorld(maxLat, lon)
          ]
        });
      }
    }
    
    // Autobahn (wenn in der Nähe von Stuttgart)
    const distToStuttgart = Math.sqrt(
      Math.pow(minLat - BW_CITIES.stuttgart.lat, 2) +
      Math.pow(minLon - BW_CITIES.stuttgart.lon, 2)
    );
    
    if (distToStuttgart < 0.2 && rng() > 0.6) {
      const angle = rng() * Math.PI;
      const centerLat = (minLat + maxLat) / 2;
      const centerLon = (minLon + maxLon) / 2;
      const length = this.tileSize * 1.5;
      
      roads.push({
        id: `motorway_${minLat}_${minLon}`,
        type: 'motorway',
        name: 'A8',
        speedLimit: 130,
        lanes: 3,
        width: ROAD_STYLES.motorway.width,
        points: [
          this.gpsToWorld(
            centerLat - Math.sin(angle) * length,
            centerLon - Math.cos(angle) * length
          ),
          this.gpsToWorld(
            centerLat + Math.sin(angle) * length,
            centerLon + Math.cos(angle) * length
          )
        ]
      });
    }
    
    return roads;
  }
  
  /**
   * Generiert prozedurale Gebäude für Demo
   */
  private generateProceduralBuildings(minLat: number, minLon: number, maxLat: number, maxLon: number): BuildingData[] {
    const buildings: BuildingData[] = [];
    const rng = this.seededRandom(minLat * 1000 + minLon * 100 + 42);
    
    // Anzahl Gebäude basierend auf Nähe zu Stadtzentrum
    const distToCity = this.getDistanceToNearestCity(minLat, minLon);
    const buildingDensity = Math.max(0, 1 - distToCity / 0.1);
    const numBuildings = Math.floor(10 + buildingDensity * 30);
    
    for (let i = 0; i < numBuildings; i++) {
      const lat = minLat + rng() * (maxLat - minLat);
      const lon = minLon + rng() * (maxLon - minLon);
      
      const width = 10 + rng() * 20;
      const depth = 10 + rng() * 20;
      const height = 5 + rng() * (buildingDensity * 30 + 10);
      
      const center = this.gpsToWorld(lat, lon);
      
      buildings.push({
        id: `building_${i}_${minLat}_${minLon}`,
        footprint: [
          new THREE.Vector3(center.x - width/2, 0, center.z - depth/2),
          new THREE.Vector3(center.x + width/2, 0, center.z - depth/2),
          new THREE.Vector3(center.x + width/2, 0, center.z + depth/2),
          new THREE.Vector3(center.x - width/2, 0, center.z + depth/2)
        ],
        height,
        type: rng() > 0.7 ? 'residential' : 'commercial'
      });
    }
    
    return buildings;
  }
  
  /**
   * Rendert ein Tile in die 3D-Szene
   */
  private renderTile(tile: MapTile): void {
    const tileKey = `${tile.x}_${tile.y}`;
    
    // Straßen rendern
    for (const road of tile.roads) {
      const mesh = this.createRoadMesh(road);
      this.roadMeshes.set(`${tileKey}_${road.id}`, mesh);
      this.scene.add(mesh);
    }
    
    // Gebäude rendern
    for (const building of tile.buildings) {
      const mesh = this.createBuildingMesh(building);
      this.buildingMeshes.set(`${tileKey}_${building.id}`, mesh);
      this.scene.add(mesh);
    }
    
    // Street furniture (lights + signals)
    this.furniture?.addFromRoads(tile.roads);
    this.furniture?.setNightFactor(this.nightFactor);
  }
  
  /**
   * Erstellt ein Straßen-Mesh
   */
  private createRoadMesh(road: RoadSegment): THREE.Mesh {
    const style = ROAD_STYLES[road.type];
    const material = this.roadMaterials.get(road.type)!;
    const halfWidth = style.width / 2;
    
    if (road.points.length < 2) {
      return new THREE.Mesh(new THREE.BufferGeometry(), material);
    }
    
    // Für jeden Segment
    const vertices: number[] = [];
    const indices: number[] = [];
    
    for (let i = 0; i < road.points.length - 1; i++) {
      const p1 = road.points[i];
      const p2 = road.points[i + 1];
      
      // Richtungsvektor
      const dir = new THREE.Vector3().subVectors(p2, p1).normalize();
      
      // Senkrechter Vektor (für Breite)
      const perp = new THREE.Vector3(-dir.z, 0, dir.x).multiplyScalar(halfWidth);
      
      // 4 Eckpunkte dieses Segments
      const idx = vertices.length / 3;
      
      vertices.push(
        p1.x - perp.x, 1.0, p1.z - perp.z,
        p1.x + perp.x, 1.0, p1.z + perp.z,
        p2.x + perp.x, 1.0, p2.z + perp.z,
        p2.x - perp.x, 1.0, p2.z - perp.z
      );
      
      // Zwei Dreiecke pro Segment
      indices.push(
        idx, idx + 1, idx + 2,
        idx, idx + 2, idx + 3
      );
    }
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.receiveShadow = true;
    mesh.userData = { type: 'road', roadData: road };

    this.addRoadMarkings(road, halfWidth);
    
    return mesh;
  }

  private addRoadMarkings(road: RoadSegment, halfWidth: number): void {
    if (road.points.length < 2) return;

    for (let i = 0; i < road.points.length - 1; i++) {
      const p1 = road.points[i];
      const p2 = road.points[i + 1];
      const segmentLength = p1.distanceTo(p2);
      if (segmentLength < 0.1) continue;

      const dir = new THREE.Vector3().subVectors(p2, p1).normalize();
      const angle = Math.atan2(dir.x, dir.z);
      const center = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

      const centerLine = new THREE.Mesh(
        new THREE.PlaneGeometry(0.15, segmentLength),
        this.roadMarkMaterial
      );
      centerLine.rotation.x = -Math.PI / 2;
      centerLine.rotation.z = angle;
      centerLine.position.set(center.x, 1.01, center.z);
      this.scene.add(centerLine);
      this.roadDecorations.push(centerLine);

      const edgeOffset = halfWidth - 0.15;
      const edgeLeft = new THREE.Mesh(
        new THREE.PlaneGeometry(0.2, segmentLength),
        this.roadEdgeMaterial
      );
      edgeLeft.rotation.x = -Math.PI / 2;
      edgeLeft.rotation.z = angle;
      edgeLeft.position.set(center.x - Math.sin(angle) * edgeOffset, 1.01, center.z - Math.cos(angle) * edgeOffset);
      this.scene.add(edgeLeft);
      this.roadDecorations.push(edgeLeft);

      const edgeRight = new THREE.Mesh(
        new THREE.PlaneGeometry(0.2, segmentLength),
        this.roadEdgeMaterial
      );
      edgeRight.rotation.x = -Math.PI / 2;
      edgeRight.rotation.z = angle;
      edgeRight.position.set(center.x + Math.sin(angle) * edgeOffset, 1.01, center.z + Math.cos(angle) * edgeOffset);
      this.scene.add(edgeRight);
      this.roadDecorations.push(edgeRight);
    }
  }
  
  /**
   * Erstellt ein Gebäude-Mesh
   */
  private createBuildingMesh(building: BuildingData): THREE.Mesh {
    if (building.footprint.length < 3) {
      return new THREE.Mesh(new THREE.BufferGeometry(), this.buildingMaterial);
    }

    const shape = new THREE.Shape();
    const fp = building.footprint;

    shape.moveTo(fp[0].x, fp[0].z);
    for (let i = 1; i < fp.length; i++) {
      shape.lineTo(fp[i].x, fp[i].z);
    }
    shape.lineTo(fp[0].x, fp[0].z);

    const height = Math.max(2, building.height);

    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: height,
      bevelEnabled: false,
      steps: 1
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.rotateX(-Math.PI / 2);

    const colorVariation = 0.86 + Math.random() * 0.22;

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(0.48 * colorVariation, 0.52 * colorVariation, 0.62 * colorVariation),
      roughness: 0.78,
      metalness: 0.06,
      emissive: new THREE.Color(0x101018),
      emissiveIntensity: 0.18
    });
    
    this.envMatCtrl.registerBuildingMaterial(mat);

    const mesh = new THREE.Mesh(geometry, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = { type: 'building', buildingData: building };

    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const w = Math.max(1, bb.max.x - bb.min.x);
    const d = Math.max(1, bb.max.z - bb.min.z);
    const roofH = Math.max(0.6, height * 0.06);

    const roofGeometry = new THREE.BoxGeometry(w * 0.98, roofH, d * 0.98);
    const roofMaterial = new THREE.MeshStandardMaterial({
      color: 0x2c2f38,
      roughness: 0.92,
      metalness: 0.04
    });

    const roof = new THREE.Mesh(roofGeometry, roofMaterial);
    roof.position.set((bb.min.x + bb.max.x) / 2, height + roofH / 2, (bb.min.z + bb.max.z) / 2);
    roof.castShadow = true;
    roof.receiveShadow = true;
    mesh.add(roof);

    return mesh;
  }
  
  /**
   * Entfernt weit entfernte Tiles aus dem Speicher
   */
  private cleanupDistantTiles(centerTileX: number, centerTileY: number): void {
    const maxDistance = this.loadRadius + 2;
    
    for (const [key, tile] of this.loadedTiles.entries()) {
      const dx = Math.abs(tile.x - centerTileX);
      const dy = Math.abs(tile.y - centerTileY);
      
      if (dx > maxDistance || dy > maxDistance) {
        // Entferne Meshes
        for (const road of tile.roads) {
          const meshKey = `${key}_${road.id}`;
          const mesh = this.roadMeshes.get(meshKey);
          if (mesh) {
            this.scene.remove(mesh);
            mesh.geometry.dispose();
            this.roadMeshes.delete(meshKey);
          }
        }
        
        for (const building of tile.buildings) {
          const meshKey = `${key}_${building.id}`;
          const mesh = this.buildingMeshes.get(meshKey);
          if (mesh) {
            this.scene.remove(mesh);
            mesh.geometry.dispose();
            (mesh.material as THREE.Material).dispose();
            this.buildingMeshes.delete(meshKey);
          }
        }
        
        this.loadedTiles.delete(key);
      }
    }
  }
  
  /**
   * Findet die nächste bekannte Stadt
   */
  private getDistanceToNearestCity(lat: number, lon: number): number {
    let minDist = Infinity;
    
    for (const city of Object.values(BW_CITIES)) {
      const dist = Math.sqrt(
        Math.pow(lat - city.lat, 2) +
        Math.pow(lon - city.lon, 2)
      );
      minDist = Math.min(minDist, dist);
    }
    
    return minDist;
  }
  
  /**
   * Seeded Random für konsistente prozedurale Generierung
   */
  private seededRandom(seed: number): () => number {
    let s = seed;
    return () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
  }
  
  /**
   * Teleportiert zu einer Stadt
   */
  teleportToCity(cityKey: keyof typeof BW_CITIES): THREE.Vector3 {
    const city = BW_CITIES[cityKey];
    this.currentPosition = { lat: city.lat, lon: city.lon };
    
    // Lade Tiles um Stadt
    this.loadAroundPosition(new THREE.Vector3(0, 0, 0));
    
    return new THREE.Vector3(0, 1, 0);
  }
  
  /**
   * Gibt GPS-Position für Anzeige zurück
   */
  getCurrentGPS(worldPos: THREE.Vector3): { lat: number; lon: number; cityName: string | null } {
    const gps = this.worldToGps(worldPos);
    
    // Finde nächste Stadt
    let nearestCity: string | null = null;
    let minDist = 0.05; // ~5km Radius
    
    for (const city of Object.values(BW_CITIES)) {
      const dist = Math.sqrt(
        Math.pow(gps.lat - city.lat, 2) +
        Math.pow(gps.lon - city.lon, 2)
      );
      if (dist < minDist) {
        minDist = dist;
        nearestCity = city.name;
      }
    }
    
    return { ...gps, cityName: nearestCity };
  }

  getNearestRoadPoint(
    worldPos: THREE.Vector3,
    maxDistance = 10
  ): { point: THREE.Vector3; roadType: RoadType; speedLimit?: number; distance: number; roadName?: string; roadWidth?: number; direction?: THREE.Vector3 } | null {
    let bestDist = maxDistance;
    let bestPoint: THREE.Vector3 | null = null;
    let bestRoad: RoadSegment | null = null;
    let bestDir: THREE.Vector3 | null = null;

    for (const tile of this.loadedTiles.values()) {
      for (const road of tile.roads) {
        for (let i = 0; i < road.points.length - 1; i++) {
          const p1 = road.points[i];
          const p2 = road.points[i + 1];
          const candidate = this.closestPointOnSegment(worldPos, p1, p2);
          const dist = candidate.distanceTo(worldPos);
          if (dist < bestDist) {
            bestDist = dist;
            bestPoint = candidate;
            bestRoad = road;
            bestDir = new THREE.Vector3().subVectors(p2, p1).normalize();
          }
        }
      }
    }

    if (!bestPoint || !bestRoad) return null;
    return {
      point: bestPoint,
      roadType: bestRoad.type,
      speedLimit: bestRoad.speedLimit,
      distance: bestDist,
      roadName: bestRoad.name,
      roadWidth: bestRoad.width,
      direction: bestDir ?? undefined
    };
  }

  private closestPointOnSegment(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 {
    const ab = new THREE.Vector3().subVectors(b, a);
    const ap = new THREE.Vector3().subVectors(p, a);
    const denom = ab.x * ab.x + ab.z * ab.z;
    if (denom <= 0.00001) return a.clone();
    const t = Math.max(0, Math.min(1, (ap.x * ab.x + ap.z * ab.z) / denom));
    return new THREE.Vector3(a.x + ab.x * t, 0, a.z + ab.z * t);
  }

  getRoadsInRadius(center: THREE.Vector3, radius: number): RoadSegment[] {
    const radiusSq = radius * radius;
    const result: RoadSegment[] = [];
    const seen = new Set<string>();

    for (const tile of this.loadedTiles.values()) {
      for (const road of tile.roads) {
        if (seen.has(road.id)) continue;
        for (const p of road.points) {
          const dx = p.x - center.x;
          const dz = p.z - center.z;
          if (dx * dx + dz * dz <= radiusSq) {
            result.push(road);
            seen.add(road.id);
            break;
          }
        }
      }
    }

    return result;
  }
  
  /**
   * Prüft ob Position auf einer Straße ist
   */
  isOnRoad(worldPos: THREE.Vector3): { onRoad: boolean; roadType?: RoadType; speedLimit?: number; roadName?: string } {
    // Raycasting nach unten um Straße zu finden
    const raycaster = new THREE.Raycaster(
      new THREE.Vector3(worldPos.x, 10, worldPos.z),
      new THREE.Vector3(0, -1, 0)
    );
    
    const roadMeshArray = Array.from(this.roadMeshes.values());
    const intersects = raycaster.intersectObjects(roadMeshArray);
    
    if (intersects.length > 0) {
      const roadData = intersects[0].object.userData.roadData as RoadSegment;
      return {
        onRoad: true,
        roadType: roadData?.type,
        speedLimit: roadData?.speedLimit || ROAD_STYLES[roadData?.type || 'residential'].speedLimit,
        roadName: roadData?.name
      };
    }
    
    return { onRoad: false };
  }
  
  /**
   * Update-Loop
   */
  update(playerPosition: THREE.Vector3): void {
    this.loadAroundPosition(playerPosition);
  }
  
  /**
   * Dispose
   */
  dispose(): void {
    // Alle Meshes entfernen
    for (const mesh of this.roadMeshes.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
    }
    
    for (const mesh of this.buildingMeshes.values()) {
      this.scene.remove(mesh);
      mesh.traverse((child) => {
        const asMesh = child as THREE.Mesh;
        if (asMesh.geometry) {
          asMesh.geometry.dispose();
        }
        if (asMesh.material) {
          if (Array.isArray(asMesh.material)) {
            asMesh.material.forEach((mat) => mat.dispose());
          } else {
            (asMesh.material as THREE.Material).dispose();
          }
        }
      });
    }
    
    // Grund entfernen
    this.scene.remove(this.groundPlane);
    this.groundPlane.geometry.dispose();
    
    // Materialien
    for (const mat of this.roadMaterials.values()) {
      mat.dispose();
    }
    this.buildingMaterial.dispose();
    this.groundMaterial.dispose();
    this.roadMarkMaterial.dispose();
    this.roadEdgeMaterial.dispose();

    for (const deco of this.roadDecorations) {
      this.scene.remove(deco);
      const mesh = deco as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) (mesh.material as THREE.Material).dispose();
    }
    
    this.loadedTiles.clear();
    this.roadMeshes.clear();
    this.buildingMeshes.clear();
  }
  
  /**
   * Set environment state for materials (night glow, wet streets)
   */
  setEnvironment(nightFactor: number, wetness: number) {
    this.nightFactor = Math.max(0, Math.min(1, nightFactor));
    this.wetness = Math.max(0, Math.min(1, wetness));

    this.envMatCtrl.setNightFactor(this.nightFactor);
    this.envMatCtrl.setWetness(this.wetness);
    this.furniture?.setNightFactor(this.nightFactor);
  }
  
  /**
   * Update animated furniture (traffic signals)
   */
  updateAnimations(dt: number) {
    this.furniture?.update(dt);
  }
}

export default OpenWorldMapLoader;
